/**
 * Standalone Quick Buy / DOD variant pills.
 * One pointer path: paint immediately, fetch after yield. Never drives variant-picker.js.
 */
(function () {
  const ROW_READY = 'qa-vpick-ready';
  const SOURCE = 'qa-vpick-source';
  const OPT = 'qa-vpick__opt';
  const OPT_ON = 'qa-vpick__opt--on';

  /** @type {HTMLElement | null} */
  let pendingOpt = null;
  let pendingToken = 0;
  let mounting = false;
  /** @type {AbortController | null} */
  let abortController = null;

  function yieldToMain() {
    if (globalThis.scheduler && typeof globalThis.scheduler.yield === 'function') {
      return globalThis.scheduler.yield();
    }
    return new Promise((resolve) => {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    });
  }

  /**
   * @param {Element | null} el
   * @returns {boolean}
   */
  function isInQuickBuy(el) {
    if (!el) return false;
    return Boolean(el.closest('#quick-add-modal-content, .quick-add-modal__content, quick-add-dialog'));
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  function getOpt(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    const opt = target.closest('.' + OPT);
    if (!(opt instanceof HTMLElement) || !isInQuickBuy(opt)) return null;
    return opt;
  }

  /**
   * @param {HTMLElement} opt
   */
  function paintOption(opt) {
    const row = opt.parentElement;
    if (!row) return;

    const opts = row.querySelectorAll('.' + OPT);
    for (let i = 0; i < opts.length; i++) {
      const el = opts[i];
      if (!(el instanceof HTMLElement)) continue;
      const selected = el === opt;
      el.classList.toggle(OPT_ON, selected);
      el.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }
  }

  /**
   * @param {HTMLElement} fromEl
   * @param {string} mode
   */
  function switchSuperbundle(fromEl, mode) {
    const scope =
      fromEl.closest('#quick-add-modal-content, quick-add-dialog, [data-product-grid-content], .shopify-section') ||
      document;
    const selector = scope.querySelector('.superbundle-selector');
    if (!selector) return false;
    const radio = selector.querySelector('input[value="' + mode + '"]');
    if (!(radio instanceof HTMLInputElement)) return false;
    if (!radio.checked) radio.checked = true;
    if (typeof window.__superbundleSwitchTo === 'function') {
      window.__superbundleSwitchTo(mode, selector);
    }
    return true;
  }

  /**
   * @param {HTMLElement} opt
   * @returns {boolean}
   */
  function switchSuperbundleIfRelated(opt) {
    const scope =
      opt.closest('#quick-add-modal-content, quick-add-dialog, [data-product-grid-content], .shopify-section') ||
      document;
    const selector = scope.querySelector('.superbundle-selector');
    if (!selector) return false;
    const bundleHandle = selector.dataset.bundleHandle || '';
    const productUrl = opt.dataset.productUrl || '';
    if (!bundleHandle || !productUrl || productUrl.indexOf('/products/' + bundleHandle) === -1) {
      return false;
    }
    return switchSuperbundle(opt, 'bundle');
  }

  /**
   * @param {Element} picker
   * @param {HTMLElement} opt
   * @returns {string}
   */
  function buildRequestUrl(picker, opt) {
    const productUrl = (opt.dataset.connectedProductUrl || picker.getAttribute('data-product-url') || '').split('?')[0];
    const ids = [];
    picker.querySelectorAll('.' + OPT_ON).forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.optionValueId) ids.push(el.dataset.optionValueId);
    });
    const params = [];
    if (ids.length) params.push('option_values=' + ids.join(','));
    else if (opt.dataset.variantId) params.push('variant=' + opt.dataset.variantId);
    return productUrl + (params.length ? '?' + params.join('&') : '');
  }

  /**
   * @param {Element} picker
   * @param {HTMLElement} opt
   * @param {Document} html
   */
  function dispatchVariantUpdate(picker, opt, html) {
    const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!textContent) return;

    let resource = null;
    try {
      resource = JSON.parse(textContent);
    } catch {
      return;
    }

    const newPicker = html.querySelector('variant-picker');
    /** @type {{ id: string, url: string } | undefined} */
    let newProduct;
    if (newPicker instanceof HTMLElement) {
      const newId = newPicker.dataset.productId || '';
      const newUrl = newPicker.dataset.productUrl || '';
      if (newId && newUrl && picker.getAttribute('data-product-id') !== newId) {
        newProduct = { id: newId, url: newUrl };
      }
      if (newId) picker.setAttribute('data-product-id', newId);
      if (newUrl) picker.setAttribute('data-product-url', newUrl);
    }

    const sourceId = opt.dataset.optionValueId || String(resource.id || '');
    picker.dispatchEvent(
      new CustomEvent('variant:update', {
        bubbles: true,
        detail: {
          resource,
          sourceId,
          data: {
            html,
            productId: picker.getAttribute('data-product-id') || '',
            newProduct,
          },
        },
      })
    );

    // Price HTML is replaced during the event. Restyle after that replacement.
    const modal = document.getElementById('quick-add-modal-content');
    const applyPromo = () => {
      if (!modal || !window.PWPdpPromo || typeof window.PWPdpPromo.applyWithin !== 'function') return;
      const raw = modal.getAttribute('data-pw-promo');
      if (!raw) return;
      try {
        window.PWPdpPromo.applyWithin(modal, JSON.parse(raw));
      } catch {
        /* ignore invalid promo payload */
      }
    };
    applyPromo();
    setTimeout(applyPromo, 0);
  }

  /**
   * @param {HTMLElement} opt
   */
  async function applyDeferred(opt) {
    if (!opt.isConnected) return;

    const picker = opt.closest('variant-picker');
    if (!picker) return;

    const isLinked =
      opt.dataset.isLinked === 'true' &&
      String(opt.dataset.productId || '') !== String(picker.getAttribute('data-product-id') || '');

    if (!isLinked) {
      switchSuperbundle(opt, 'regular');
    } else if (switchSuperbundleIfRelated(opt)) {
      return;
    } else {
      const productUrl = opt.dataset.productUrl || '';
      const variantId = opt.dataset.variantId || '';
      if (!productUrl || typeof window.PWQuickAddModal?.swapProduct !== 'function') return;
      const target = new URL(productUrl, window.location.origin);
      if (variantId) target.searchParams.set('variant', String(variantId));
      await window.PWQuickAddModal.swapProduct(target.toString(), { variantId });
      return;
    }

    const variantId = opt.dataset.variantId || '';
    const modal = document.getElementById('quick-add-modal-content');
    const idInput = modal?.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    if (idInput instanceof HTMLInputElement && variantId) {
      idInput.disabled = false;
      idInput.value = variantId;
    }

    picker.dispatchEvent(
      new CustomEvent('variant:selected', {
        bubbles: true,
        detail: { resource: { id: opt.dataset.optionValueId || variantId } },
      })
    );

    const requestUrl = buildRequestUrl(picker, opt);
    if (!requestUrl || requestUrl === '?') return;

    abortController?.abort();
    abortController = new AbortController();
    const signal = abortController.signal;

    try {
      const response = await fetch(requestUrl, { signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      dispatchVariantUpdate(picker, opt, html);
    } catch (error) {
      if (error?.name !== 'AbortError') console.error(error);
    }
  }

  /**
   * @param {HTMLElement} opt
   */
  function scheduleDeferred(opt) {
    if (pendingOpt === opt && pendingToken !== 0) return;
    pendingOpt = opt;
    const token = ++pendingToken;
    yieldToMain().then(() => {
      if (token !== pendingToken || pendingOpt !== opt) return;
      pendingOpt = null;
      applyDeferred(opt);
    });
  }

  /**
   * @param {HTMLInputElement} native
   * @param {HTMLElement} opt
   */
  function copyNativeData(native, opt) {
    const keys = [
      'optionValueId',
      'variantId',
      'connectedProductUrl',
      'productUrl',
      'productId',
      'isLinked',
      'variantOptions',
      'state',
      'optionAvailable',
      'available',
    ];
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const value = native.dataset[key];
      if (value) opt.dataset[key] = value;
    }
  }

  /**
   * @param {Element} row
   */
  function mountRow(row) {
    if (!(row instanceof HTMLElement)) return;
    if (row.classList.contains(ROW_READY)) return;
    if (row.closest('.' + SOURCE)) return;
    if (row.closest('.linked-products__hidden-options')) return;
    if (row.closest('.superbundle-selector')) return;

    const labels = row.querySelectorAll(':scope > label.variant-option__button-label');
    if (!labels.length) return;

    const frag = document.createDocumentFragment();
    const source = document.createElement('div');
    source.className = SOURCE;
    source.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      if (!(label instanceof HTMLElement)) continue;

      const native = label.querySelector('input[type="radio"]');
      if (!(native instanceof HTMLInputElement)) continue;

      const selected = native.checked || native.dataset.currentChecked === 'true';
      const unavailable =
        native.dataset.optionAvailable === 'false' ||
        native.dataset.state === 'soldout' ||
        native.dataset.state === 'unavailable' ||
        native.dataset.available === 'false';
      const missing = native.disabled || (native.dataset.state === 'missing' && !native.dataset.variantId);

      const opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'variant-option__button-label ' + OPT;
      if (selected) opt.classList.add(OPT_ON);
      if (unavailable) opt.classList.add('qa-vpick__opt--off');
      if (missing) opt.classList.add('qa-vpick__opt--missing');
      opt.setAttribute('aria-pressed', selected ? 'true' : 'false');
      copyNativeData(native, opt);

      const textNode = label.querySelector('.variant-option__button-label__text');
      const aria = (native.getAttribute('aria-label') || native.value || '').trim();
      const text = document.createElement('span');
      text.className = 'variant-option__button-label__text qa-vpick__text';
      text.textContent = (textNode && textNode.textContent ? textNode.textContent : aria).trim();
      if (aria) opt.setAttribute('aria-label', aria);

      const swatch = label.querySelector('.swatch');
      if (swatch) {
        opt.classList.add('qa-vpick__opt--swatch');
        opt.classList.add('variant-option__button-label--has-swatch');
        opt.appendChild(swatch.cloneNode(true));
      }

      opt.appendChild(text);
      const strike = label.querySelector(':scope > svg');
      if (strike) opt.appendChild(strike.cloneNode(true));
      frag.appendChild(opt);
      source.appendChild(label);
    }

    if (!source.childElementCount) return;

    row.appendChild(source);
    row.appendChild(frag);
    row.classList.add(ROW_READY);
  }

  /**
   * @param {Element | null} root
   */
  function mount(root) {
    if (!root || mounting) return;
    const picker = root.querySelector('variant-picker');
    if (!picker) return;

    mounting = true;
    try {
      const rows = picker.querySelectorAll(
        'fieldset.variant-option, [data-linked-option-row], [data-linked-main-variants]'
      );
      for (let i = 0; i < rows.length; i++) {
        mountRow(rows[i]);
      }
    } finally {
      mounting = false;
    }
  }

  let lastTouchAt = 0;
  let lastHandledAt = 0;
  /** @type {AbortController | null} */
  let bindController = null;

  /**
   * One JS path: pointerdown only.
   * A tap always produces native pointerdown + pointerup; we must not listen to pointerup
   * or Chrome's Interactions track shows a second Pointer with our code.
   * Also ignore the compatibility mouse pointer that follows a touch.
   * @param {Event} event
   */
  function onPointerDown(event) {
    if (!(event instanceof PointerEvent)) return;
    if (event.button !== 0) return;
    if (event.isPrimary === false) return;

    const opt = getOpt(event);
    if (!opt) return;

    if (event.pointerType === 'touch') {
      lastTouchAt = performance.now();
    } else if (event.pointerType === 'mouse' && performance.now() - lastTouchAt < 700) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (opt.classList.contains('qa-vpick__opt--missing')) return;

    const now = performance.now();
    if (now - lastHandledAt < 250 && pendingOpt === opt) return;
    lastHandledAt = now;

    paintOption(opt);
    scheduleDeferred(opt);
  }

  function observeContent() {
    const content = document.getElementById('quick-add-modal-content');
    if (!content || content.dataset.qaVpickObserved === 'true') {
      if (content) mount(content);
      return;
    }

    content.dataset.qaVpickObserved = 'true';
    const observer = new MutationObserver(() => {
      mount(content);
    });
    observer.observe(content, { childList: true, subtree: true });
    mount(content);
  }

  function unbind() {
    bindController?.abort();
    bindController = null;
  }

  function bind() {
    unbind();
    bindController = new AbortController();
    const opts = { capture: true, signal: bindController.signal };
    const dialog = document.getElementById('quick-add-dialog');
    if (dialog) dialog.addEventListener('pointerdown', onPointerDown, opts);
    else document.addEventListener('pointerdown', onPointerDown, opts);
    observeContent();
  }

  if (typeof window.__pwQaVpickUnbind === 'function') {
    window.__pwQaVpickUnbind();
  }
  window.__pwQaVpickUnbind = unbind;
  window.PWQuickAddVariantPicker = { mount: mount, bind: bind };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
